import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useData } from '../lib/data';
import { useSelection } from '../lib/selection';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Client, Project, Task, TaskPriority } from '../lib/database.types';
import { DueChip } from '../components/DueChip';

const PRIORITY_WEIGHT: Record<TaskPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const ACTIVE = new Set(['pending', 'in_progress', 'blocked']);
const HIDDEN_STATES = new Set(['archived', 'completed']);

// Read custom sort order from the jsonb context field. Fallback to the
// default order (server-side by display_name for clients).
function clientSortOrder(c: Client): number {
  const ctx = c.context as Record<string, unknown> | undefined;
  const v = ctx?.sort_order;
  return typeof v === 'number' ? v : Number.POSITIVE_INFINITY;
}

function isSameLocalDay(iso: string, target: Date): boolean {
  const d = new Date(iso);
  return (
    d.getFullYear() === target.getFullYear() &&
    d.getMonth() === target.getMonth() &&
    d.getDate() === target.getDate()
  );
}

export function Home() {
  const {
    tasks,
    clients,
    projects,
    projectBySlug,
    clientBySlug,
    subClientsByParent,
    isTopLevelClient,
  } = useData();
  const { select } = useSelection();
  const { push } = useToast();

  // Collapsed state for sub-client groups (local to Home, not persisted
  // across page refreshes — use sidebar expanded state for that).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapsed = (slug: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  const visibleProjects = useMemo(
    () => projects.filter((p) => !HIDDEN_STATES.has(p.state)),
    [projects],
  );

  const activeTopTasks = useMemo(
    () => tasks.filter((t) => t.parent_task_id === null && ACTIVE.has(t.status)),
    [tasks],
  );

  const countActiveForClient = (c: Client): number => {
    const direct = visibleProjects
      .filter((p) => p.client_slug === c.slug)
      .reduce((sum, p) => {
        return sum + activeTopTasks.filter((t) => t.project_slug === p.slug).length;
      }, 0);
    const subClients = subClientsByParent.get(c.slug) ?? [];
    const indirect = subClients.reduce((sum, sc) => sum + countActiveForClient(sc), 0);
    return direct + indirect;
  };

  const topLevelClients = useMemo(
    () =>
      clients
        .filter(isTopLevelClient)
        .filter((c) => c.slug !== 'shwetzer')
        .sort((a, b) => {
          const oa = clientSortOrder(a);
          const ob = clientSortOrder(b);
          if (oa !== ob) return oa - ob;
          return a.display_name.localeCompare(b.display_name);
        }),
    [clients, isTopLevelClient],
  );

  const stats = useMemo(() => {
    return {
      activeTotal: activeTopTasks.length,
      critical: activeTopTasks.filter((t) => t.priority === 'critical').length,
      blocked: activeTopTasks.filter((t) => t.status === 'blocked').length,
      companyCount: topLevelClients.length,
      buildCount: visibleProjects.filter((p) => p.client_slug).length,
    };
  }, [activeTopTasks, topLevelClients, visibleProjects]);

  const completedTasks = useMemo(
    () =>
      tasks
        .filter((t) => t.status === 'completed' && t.completed_at)
        .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? '')),
    [tasks],
  );

  const completedToday = useMemo(() => {
    const today = new Date();
    return completedTasks.filter((t) => t.completed_at && isSameLocalDay(t.completed_at, today));
  }, [completedTasks]);

  // Priority queue — sorted by (1) explicit priority_rank (drag order),
  // (2) priority enum weight, (3) due date, (4) created_at.
  const priorityQueue = useMemo(() => {
    return [...activeTopTasks]
      .sort((a, b) => {
        const ra = a.priority_rank ?? Number.POSITIVE_INFINITY;
        const rb = b.priority_rank ?? Number.POSITIVE_INFINITY;
        if (ra !== rb) return ra - rb;
        const pw = PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
        if (pw !== 0) return pw;
        if (a.due_at && b.due_at) return a.due_at.localeCompare(b.due_at);
        if (a.due_at) return -1;
        if (b.due_at) return 1;
        return a.created_at.localeCompare(b.created_at);
      })
      .slice(0, 10);
  }, [activeTopTasks]);

  const [localCompanies, setLocalCompanies] = useState<Client[]>(topLevelClients);
  useEffect(() => setLocalCompanies(topLevelClients), [topLevelClients]);

  const [localQueue, setLocalQueue] = useState<Task[]>(priorityQueue);
  useEffect(() => setLocalQueue(priorityQueue), [priorityQueue]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onCompanyDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = localCompanies.findIndex((c) => c.slug === active.id);
    const newIndex = localCompanies.findIndex((c) => c.slug === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const previous = localCompanies;
    const reordered = arrayMove(localCompanies, oldIndex, newIndex);
    setLocalCompanies(reordered);
    if (READONLY) return;
    try {
      // Persist as sort_order inside tb_clients.context jsonb. No schema change.
      const results = await Promise.all(
        reordered.map((c, i) => {
          const ctx = { ...(c.context as object), sort_order: i };
          return supabase.from('tb_clients').update({ context: ctx }).eq('slug', c.slug);
        }),
      );
      const failure = results.find((r) => r.error);
      if (failure?.error) throw failure.error;
    } catch (error) {
      setLocalCompanies(previous);
      push({
        kind: 'error',
        title: 'Failed to reorder companies',
        body: error instanceof Error ? error.message : 'Unknown reorder error',
        ttlMs: 5000,
      });
    }
  };

  const onTaskDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = localQueue.findIndex((t) => t.id === active.id);
    const newIndex = localQueue.findIndex((t) => t.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const previous = localQueue;
    const reordered = arrayMove(localQueue, oldIndex, newIndex);
    setLocalQueue(reordered);
    if (READONLY) return;
    try {
      const results = await Promise.all(
        reordered.map((t, i) =>
          supabase.from('tb_tasks').update({ priority_rank: i + 1 }).eq('id', t.id),
        ),
      );
      const failure = results.find((r) => r.error);
      if (failure?.error) throw failure.error;
    } catch (error) {
      setLocalQueue(previous);
      push({
        kind: 'error',
        title: 'Failed to reorder priority queue',
        body: error instanceof Error ? error.message : 'Unknown reorder error',
        ttlMs: 5000,
      });
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl mx-auto p-8 space-y-8">
        <header className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Home</h1>
          <p className="text-sm text-muted font-mono">
            {stats.activeTotal} active · {stats.companyCount} companies · {stats.buildCount} builds
            {stats.blocked > 0 && (
              <>
                {' '}
                · <span className="text-red">{stats.blocked} blocked</span>
              </>
            )}
          </p>
        </header>

        <section className="panel p-4 space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <div className="text-[11px] font-mono tracking-widest text-green uppercase">
                Completed Today
              </div>
              <div className="text-2xl font-semibold mt-1">{completedToday.length}</div>
            </div>
            <button
              onClick={() => select({ type: 'completed' })}
              className="text-xs font-mono text-dim hover:text-ink underline"
            >
              view all
            </button>
          </div>
          <div className="space-y-1.5">
            {completedToday.slice(0, 3).map((task) => (
              <button
                key={task.id}
                onClick={() => select({ type: 'task', id: task.id })}
                className="w-full panel-hi px-3 py-2 text-left hover:border-green/40 transition-colors"
              >
                <div className="text-sm text-ink line-through decoration-green/70">
                  {task.title}
                </div>
                <div className="text-[11px] text-dim font-mono mt-1">
                  {(task.completed_at && new Date(task.completed_at).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  })) || 'completed'}
                </div>
              </button>
            ))}
            {completedToday.length === 0 && (
              <div className="text-sm text-dim font-mono">nothing completed yet today</div>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-[11px] font-mono tracking-widest text-amberHi uppercase">
              Companies
            </h2>
            <span className="text-xs text-dim font-mono">drag to reorder</span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onCompanyDragEnd}
          >
            <SortableContext
              items={localCompanies.map((c) => c.slug)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1.5">
                {localCompanies.map((c) => {
                  const cProjects = visibleProjects.filter((p) => p.client_slug === c.slug);
                  const subClients = subClientsByParent.get(c.slug) ?? [];
                  const directActive = activeTopTasks.filter((t) =>
                    cProjects.some((p) => p.slug === t.project_slug),
                  );
                  const rolledActive = countActiveForClient(c);
                  const critical = directActive.filter((t) => t.priority === 'critical').length;
                  const blocked = directActive.filter((t) => t.status === 'blocked').length;
                  const isCollapsed = collapsed.has(c.slug);
                  return (
                    <SortableCompany
                      key={c.slug}
                      client={c}
                      projectCount={cProjects.length}
                      activeCount={rolledActive}
                      criticalCount={critical}
                      blockedCount={blocked}
                      subClientCount={subClients.length}
                      isCollapsed={isCollapsed}
                      onToggle={() => toggleCollapsed(c.slug)}
                      onOpen={() => select({ type: 'client', slug: c.slug })}
                    >
                      {!isCollapsed && subClients.length > 0 && (
                        <div className="ml-6 border-l border-border pl-3 mt-1 space-y-1.5">
                          {subClients.map((sc) => {
                            const scProjects = visibleProjects.filter(
                              (p) => p.client_slug === sc.slug,
                            );
                            const scActive = activeTopTasks.filter((t) =>
                              scProjects.some((p) => p.slug === t.project_slug),
                            );
                            const scCritical = scActive.filter(
                              (t) => t.priority === 'critical',
                            ).length;
                            const scBlocked = scActive.filter(
                              (t) => t.status === 'blocked',
                            ).length;
                            return (
                              <CompanyRow
                                key={sc.slug}
                                client={sc}
                                projectCount={scProjects.length}
                                activeCount={scActive.length}
                                criticalCount={scCritical}
                                blockedCount={scBlocked}
                                subClientCount={0}
                                isSubClient
                                onOpen={() => select({ type: 'client', slug: sc.slug })}
                              />
                            );
                          })}
                        </div>
                      )}
                    </SortableCompany>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </section>

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-[11px] font-mono tracking-widest text-amberHi uppercase">
              Priority
            </h2>
            <span className="text-xs text-dim font-mono">top 10 · drag to reorder</span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onTaskDragEnd}
          >
            <SortableContext
              items={localQueue.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-1.5">
                {localQueue.map((t) => (
                  <SortablePriorityRow
                    key={t.id}
                    task={t}
                    project={projectBySlug[t.project_slug]}
                    client={
                      projectBySlug[t.project_slug]?.client_slug
                        ? clientBySlug[projectBySlug[t.project_slug].client_slug!]
                        : undefined
                    }
                    onOpen={() => select({ type: 'task', id: t.id })}
                  />
                ))}
                {localQueue.length === 0 && (
                  <div className="panel p-8 text-center text-dim font-mono text-sm">
                    nothing active
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </section>
      </div>
    </div>
  );
}

// Sortable wrapper around a company row. Reordering persists client sort_order
// back to tb_clients.context jsonb so sidebar + home stay in sync.
function SortableCompany({
  client,
  children,
  ...rowProps
}: {
  client: Client;
  projectCount: number;
  activeCount: number;
  criticalCount: number;
  blockedCount: number;
  subClientCount: number;
  isCollapsed: boolean;
  onToggle: () => void;
  onOpen: () => void;
  children?: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: client.slug,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <CompanyRow client={client} {...rowProps} dragHandleProps={{ ...attributes, ...listeners }} />
      {children}
    </div>
  );
}

function CompanyRow({
  client,
  projectCount,
  activeCount,
  criticalCount,
  blockedCount,
  subClientCount,
  isCollapsed,
  isSubClient = false,
  onToggle,
  onOpen,
  dragHandleProps,
}: {
  client: Client;
  projectCount: number;
  activeCount: number;
  criticalCount: number;
  blockedCount: number;
  subClientCount: number;
  isCollapsed?: boolean;
  isSubClient?: boolean;
  onToggle?: () => void;
  onOpen: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
}) {
  const hasToggleControl = subClientCount > 0 && onToggle;
  return (
    <div
      className={
        'panel flex items-center gap-3 px-3 py-3 transition-colors ' +
        'hover:border-amber/60 hover:bg-panelHi/40 ' +
        (isSubClient ? 'bg-panel/40' : '')
      }
    >
      {/* Drag handle */}
      {dragHandleProps && (
        <button
          {...dragHandleProps}
          className="text-dim hover:text-ink cursor-grab active:cursor-grabbing px-1 shrink-0"
          aria-label="drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >
          ⋮⋮
        </button>
      )}

      {/* Expand/collapse chevron — only present if this client has sub-clients */}
      {hasToggleControl ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="text-dim hover:text-ink shrink-0 w-5 text-center"
          aria-label={isCollapsed ? 'expand' : 'collapse'}
        >
          <span className={'inline-block transition-transform ' + (!isCollapsed ? 'rotate-90' : '')}>
            ▸
          </span>
        </button>
      ) : (
        <span className="w-5 shrink-0" />
      )}

      <button onClick={onOpen} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{client.display_name}</span>
          {isSubClient && <span className="text-[10px] font-mono text-dim">sub</span>}
        </div>
        {client.primary_contact && (
          <div className="text-[11px] font-mono text-muted truncate mt-0.5">
            {client.primary_contact}
            {client.primary_role ? ` · ${client.primary_role}` : ''}
          </div>
        )}
      </button>

      <div className="flex items-center gap-3 shrink-0 text-xs font-mono tabular-nums">
        {projectCount > 0 && (
          <Stat label="builds" value={projectCount} cls="text-ink" />
        )}
        {subClientCount > 0 && (
          <Stat label="subs" value={subClientCount} cls="text-ink" />
        )}
        <Stat
          label="active"
          value={activeCount}
          cls={activeCount > 0 ? 'text-amberHi' : 'text-dim'}
        />
        {criticalCount > 0 && <Stat label="crit" value={criticalCount} cls="text-red" />}
        {blockedCount > 0 && <Stat label="blk" value={blockedCount} cls="text-red" />}
      </div>
    </div>
  );
}

function Stat({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="text-right">
      <div className="text-dim text-[9px] uppercase tracking-widest">{label}</div>
      <div className={cls}>{value}</div>
    </div>
  );
}

function SortablePriorityRow({
  task,
  project,
  client,
  onOpen,
}: {
  task: Task;
  project?: Project;
  client?: Client;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });
  const { patchTaskLocal } = useData();
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  const completeTask = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (READONLY) return;
    setBusy(true);
    const now = new Date().toISOString();
    await updateTaskWithRollback({
      task,
      patch: { status: 'completed', completed_at: now },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ status: 'completed', completed_at: now })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task completed',
        body: task.title.slice(0, 60),
        ttlMs: 2500,
      },
      errorTitle: 'Failed to complete task',
    });
    setBusy(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className="panel p-3 flex items-center gap-3
                 hover:border-borderHi hover:bg-panelHi/40 transition-colors"
    >
      <button
        {...attributes}
        {...listeners}
        className="text-dim hover:text-ink cursor-grab active:cursor-grabbing px-1 shrink-0"
        aria-label="drag"
        onClick={(e) => e.stopPropagation()}
      >
        ⋮⋮
      </button>

      <button
        onClick={completeTask}
        disabled={busy}
        aria-label="complete task"
        className="check-hit"
        title="mark complete"
      >
        <span className="check-dot" />
      </button>

      <button onClick={onOpen} className="flex-1 min-w-0 text-left cursor-pointer">
        <div className="text-sm font-medium truncate leading-tight">{task.title}</div>
        <div className="flex items-center gap-2 text-xs text-muted font-mono mt-1 flex-wrap">
          {client && (
            <>
              <span className="text-amberHi">{client.display_name}</span>
              <span className="text-dim">›</span>
            </>
          )}
          <span className="truncate">{project?.name ?? task.project_slug}</span>
          {task.status === 'blocked' && (
            <>
              <span className="text-dim">·</span>
              <span className="chip chip-critical">blocked</span>
            </>
          )}
          {task.status === 'in_progress' && (
            <>
              <span className="text-dim">·</span>
              <span className="chip chip-high">in progress</span>
            </>
          )}
        </div>
      </button>

      <div className="flex items-center gap-1.5 shrink-0">
        <span className={`chip chip-${task.priority}`}>{task.priority}</span>
        <DueChip dueAt={task.due_at} />
      </div>
    </div>
  );
}
